/**
 * Stock Manager Component
 *
 * Comprehensive stock management with reservation, release, and adjustment capabilities
 */

import {
  ArrowsClockwiseIcon,
  LockIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { useMemo, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import { useToast } from '@/hooks/use-toast';
import { DataTableColumn } from '@/types/datatable';
import { InventoryItem, StockReservation } from '@/types/inventory';
import { formatCurrency, formatDate, formatNumber } from '@/utils';

interface StockManagerProps {
  inventoryItem: InventoryItem;
  onStockUpdate?: () => void;
  className?: string;
}

interface StockReservationRecord extends StockReservation {
  id: string;
  status: 'active' | 'released' | 'expired';
  createdAt: string;
  updatedAt?: string;
  customerName?: string;
}

interface StockAdjustmentData {
  quantity: number;
  reason: 'stock_adjustment' | 'received_goods' | 'damaged_goods' | 'return';
  reference: string;
  notes: string;
  updateCost?: {
    costPrice: number;
    reason: string;
  };
}

interface ReservationFormData {
  quantity: number;
  orderId: string;
  variantIndex?: number;
  reservationId: string;
  notes: string;
  expiresAt: string;
  customerName: string;
}

const ADJUSTMENT_REASONS = [
  { value: 'stock_adjustment', label: 'Stock Adjustment' },
  { value: 'received_goods', label: 'Received Goods' },
  { value: 'damaged_goods', label: 'Damaged Goods' },
  { value: 'return', label: 'Product Return' },
];

export const StockManager: React.FC<StockManagerProps> = ({
  inventoryItem,
  onStockUpdate,
  className = '',
}) => {
  const api = useEnhancedApi();
  const { toast } = useToast();

  const showToast = (message: string, isSuccess: boolean) => {
    toast({
      title: isSuccess ? 'Success' : 'Error',
      description: message,
      variant: isSuccess ? 'default' : 'destructive',
    });
  };
  const [reservations, setReservations] = useState<StockReservationRecord[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [activeSheet, setActiveSheet] = useState<
    'reserve' | 'adjust' | 'release' | null
  >(null);
  const [_selectedReservation, _setSelectedReservation] =
    useState<StockReservationRecord | null>(null);

  // Form states
  const [reservationForm, setReservationForm] = useState<ReservationFormData>({
    quantity: 0,
    orderId: '',
    variantIndex: undefined,
    reservationId: '',
    notes: '',
    expiresAt: '',
    customerName: '',
  });

  const [adjustmentForm, setAdjustmentForm] = useState<StockAdjustmentData>({
    quantity: inventoryItem.quantity,
    reason: 'stock_adjustment',
    reference: '',
    notes: '',
  });

  // Calculate stock statistics
  const stockStats = useMemo(() => {
    const totalReserved = reservations
      .filter(r => r.status === 'active')
      .reduce((sum, r) => sum + r.quantity, 0);
    const availableStock = inventoryItem.quantity - totalReserved;
    const expiredReservations = reservations.filter(
      r => r.status === 'expired'
    ).length;
    const activeReservations = reservations.filter(
      r => r.status === 'active'
    ).length;

    return {
      totalStock: inventoryItem.quantity,
      reservedStock: totalReserved,
      availableStock: Math.max(0, availableStock),
      activeReservations,
      expiredReservations,
      utilizationRate:
        inventoryItem.quantity > 0
          ? (totalReserved / inventoryItem.quantity) * 100
          : 0,
    };
  }, [inventoryItem.quantity, reservations]);

  // Table columns for reservations
  const reservationColumns: DataTableColumn<StockReservationRecord>[] = [
    {
      id: 'reservationId',
      header: 'Reservation ID',
      accessor: 'reservationId',
      cell: ({ row }) => (
        <div className='font-medium'>
          <div className='flex items-center gap-2'>
            <LockIcon className='h-4 w-4 text-blue-500' />
            {(row as any).reservationId || (row as any).id}
          </div>
        </div>
      ),
    },
    {
      id: 'customerName',
      header: 'Customer',
      accessor: 'customerName',
      cell: ({ row }) => (
        <span className='text-gray-900'>
          {(row as any).customerName || 'N/A'}
        </span>
      ),
    },
    {
      id: 'quantity',
      header: 'Reserved Qty',
      accessor: 'quantity',
      cell: ({ row }) => (
        <div className='flex items-center gap-2'>
          <span className='font-medium text-orange-600'>
            {formatNumber((row as any).quantity)}{' '}
            {String(inventoryItem.measurementUnit) || 'units'}
          </span>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      cell: ({ row }) => {
        const statusColors = {
          active: 'bg-green-100 text-green-800',
          released: 'bg-gray-100 text-gray-800',
          expired: 'bg-red-100 text-red-800',
        };
        const key = ((row as any).status ||
          'active') as keyof typeof statusColors;
        return (
          <Badge className={statusColors[key] || 'bg-gray-100 text-gray-800'}>
            {(row as any).status.charAt(0).toUpperCase() +
              (row as any).status.slice(1)}
          </Badge>
        );
      },
    },
    {
      id: 'expiresAt',
      header: 'Expires',
      accessor: 'expiresAt',
      cell: ({ row }) => {
        const rowData = row as any;
        if (!rowData.expiresAt)
          return <span className='text-gray-500'>Never</span>;
        const expiryDate = new Date(rowData.expiresAt);
        const isExpired = expiryDate < new Date();
        return (
          <span className={isExpired ? 'text-red-600 font-medium' : ''}>
            {formatDate(rowData.expiresAt)}
          </span>
        );
      },
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessor: 'createdAt',
      cell: ({ row }) => (
        <span className='text-gray-600 text-sm'>
          {formatDate((row as any).createdAt)}
        </span>
      ),
    },
  ];

  // Generate reservation ID
  const generateReservationId = () => {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 8);
    const reservationId = `RESV-${timestamp}-${randomStr}`.toUpperCase();
    setReservationForm(prev => ({ ...prev, reservationId }));
  };

  // Generate reference number for adjustment
  const generateAdjustmentRef = () => {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 6);
    const reference = `ADJ-${timestamp}-${randomStr}`.toUpperCase();
    setAdjustmentForm(prev => ({ ...prev, reference }));
  };

  // Handle stock reservation
  const handleReserveStock = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const reservationData = {
        inventoryId: inventoryItem._id!,
        quantity: reservationForm.quantity,
        orderId: reservationForm.orderId,
        variantIndex: reservationForm.variantIndex,
        reservationId: reservationForm.reservationId,
        notes: reservationForm.notes,
        expiresAt: reservationForm.expiresAt || undefined,
      };

      await api.post('/inventories/reserve', reservationData);

      // Add to local state
      const newReservation: StockReservationRecord = {
        ...reservationData,
        id: reservationForm.reservationId,
        status: 'active',
        createdAt: new Date().toISOString(),
        customerName: reservationForm.customerName,
      };
      setReservations(prev => [...prev, newReservation]);

      showToast('Stock reserved successfully', true);
      setActiveSheet(null);
      setReservationForm({
        quantity: 0,
        orderId: '',
        variantIndex: undefined,
        reservationId: '',
        notes: '',
        expiresAt: '',
        customerName: '',
      });
      onStockUpdate?.();
    } catch (error) {
      showToast('Failed to reserve stock', false);
    } finally {
      setLoading(false);
    }
  };

  // Handle stock release
  const handleReleaseStock = async (
    reservation: StockReservationRecord,
    reason: string
  ) => {
    setLoading(true);

    try {
      const releaseData = {
        inventoryId: inventoryItem._id!,
        quantity: reservation.quantity,
        orderId: reservation.orderId,
        reservationId: reservation.reservationId,
        reason,
      };

      await api.post('/inventories/release', releaseData);

      // Update local state
      setReservations(prev =>
        prev.map(r =>
          r.id === reservation.id
            ? {
                ...r,
                status: 'released' as const,
                updatedAt: new Date().toISOString(),
              }
            : r
        )
      );

      showToast('Stock released successfully', true);
      onStockUpdate?.();
    } catch (error) {
      showToast('Failed to release stock', false);
    } finally {
      setLoading(false);
    }
  };

  // Handle stock adjustment
  const handleStockAdjustment = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.put(`/inventories/${inventoryItem._id}`, adjustmentForm);

      showToast('Stock adjusted successfully', true);
      setActiveSheet(null);
      setAdjustmentForm({
        quantity: inventoryItem.quantity,
        reason: 'stock_adjustment',
        reference: '',
        notes: '',
      });
      onStockUpdate?.();
    } catch (error) {
      showToast('Failed to adjust stock', false);
    } finally {
      setLoading(false);
    }
  };

  // Row actions for reservations
  const reservationRowActions = [
    {
      key: 'release',
      label: 'Release Stock',
      onClick: (row: StockReservationRecord) =>
        handleReleaseStock(row, 'cancellation'),
      show: (row: StockReservationRecord) => row.status === 'active',
    },
    {
      key: 'view',
      label: 'View Details',
      onClick: (row: StockReservationRecord) => {
        _setSelectedReservation(row);
      },
    },
  ];

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Stock Statistics */}
      <div className='grid grid-cols-1 md:grid-cols-6 gap-4'>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Total Stock</div>
            <div className='text-2xl font-bold text-blue-600'>
              {formatNumber(stockStats.totalStock)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Available</div>
            <div className='text-2xl font-bold text-green-600'>
              {formatNumber(stockStats.availableStock)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Reserved</div>
            <div className='text-2xl font-bold text-orange-600'>
              {formatNumber(stockStats.reservedStock)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>
              Active Reservations
            </div>
            <div className='text-2xl font-bold'>
              {stockStats.activeReservations}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Utilization</div>
            <div className='text-2xl font-bold text-purple-600'>
              {Math.round(stockStats.utilizationRate)}%
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Value</div>
            <div className='text-2xl font-bold text-green-600'>
              {formatCurrency(
                stockStats.totalStock * (inventoryItem.price || 0)
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Stock Operations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='flex flex-wrap gap-3'>
            <Sheet
              open={activeSheet === 'reserve'}
              onOpenChange={open => setActiveSheet(open ? 'reserve' : null)}
            >
              <SheetTrigger asChild>
                <Button onClick={generateReservationId}>
                  <LockIcon className='h-4 w-4 mr-2' />
                  Reserve Stock
                </Button>
              </SheetTrigger>
              <SheetContent className='w-full sm:max-w-lg'>
                <SheetHeader>
                  <SheetTitle>Reserve Stock</SheetTitle>
                </SheetHeader>
                <form onSubmit={handleReserveStock} className='space-y-4 mt-6'>
                  <div className='space-y-2'>
                    <Label htmlFor='quantity'>Quantity to Reserve *</Label>
                    <Input
                      id='quantity'
                      type='number'
                      value={reservationForm.quantity}
                      onChange={e =>
                        setReservationForm(prev => ({
                          ...prev,
                          quantity: parseFloat(e.target.value) || 0,
                        }))
                      }
                      max={stockStats.availableStock}
                      min='0'
                      step='0.01'
                      required
                    />
                    <div className='text-xs text-gray-500'>
                      Available: {formatNumber(stockStats.availableStock)}{' '}
                      {String(inventoryItem.measurementUnit) || 'units'}
                    </div>
                  </div>

                  <div className='grid grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label htmlFor='customerName'>Customer Name</Label>
                      <Input
                        id='customerName'
                        value={reservationForm.customerName}
                        onChange={e =>
                          setReservationForm(prev => ({
                            ...prev,
                            customerName: e.target.value,
                          }))
                        }
                        placeholder='Customer or company name'
                      />
                    </div>
                    <div className='space-y-2'>
                      <Label htmlFor='orderId'>Order ID</Label>
                      <Input
                        id='orderId'
                        value={reservationForm.orderId}
                        onChange={e =>
                          setReservationForm(prev => ({
                            ...prev,
                            orderId: e.target.value,
                          }))
                        }
                        placeholder='Related order ID'
                      />
                    </div>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='reservationId'>Reservation ID</Label>
                    <div className='flex gap-2'>
                      <Input
                        id='reservationId'
                        value={reservationForm.reservationId}
                        onChange={e =>
                          setReservationForm(prev => ({
                            ...prev,
                            reservationId: e.target.value,
                          }))
                        }
                        placeholder='Auto-generated'
                      />
                      <Button
                        type='button'
                        hierarchy='outlined'
                        onClick={generateReservationId}
                      >
                        Generate
                      </Button>
                    </div>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='expiresAt'>Expiry Date (Optional)</Label>
                    <Input
                      id='expiresAt'
                      type='datetime-local'
                      value={reservationForm.expiresAt}
                      onChange={e =>
                        setReservationForm(prev => ({
                          ...prev,
                          expiresAt: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='reservationNotes'>Notes</Label>
                    <Textarea
                      id='reservationNotes'
                      value={reservationForm.notes}
                      onChange={e =>
                        setReservationForm(prev => ({
                          ...prev,
                          notes: e.target.value,
                        }))
                      }
                      placeholder='Reservation notes...'
                      rows={3}
                    />
                  </div>

                  <Separator />

                  <div className='flex justify-end gap-2'>
                    <Button
                      type='button'
                      hierarchy='outlined'
                      onClick={() => setActiveSheet(null)}
                    >
                      Cancel
                    </Button>
                    <Button type='submit' loading={loading}>
                      Reserve Stock
                    </Button>
                  </div>
                </form>
              </SheetContent>
            </Sheet>

            <Sheet
              open={activeSheet === 'adjust'}
              onOpenChange={open => setActiveSheet(open ? 'adjust' : null)}
            >
              <SheetTrigger asChild>
                <Button hierarchy='outlined' onClick={generateAdjustmentRef}>
                  <ArrowsClockwiseIcon className='h-4 w-4 mr-2' />
                  Adjust Stock
                </Button>
              </SheetTrigger>
              <SheetContent className='w-full sm:max-w-lg'>
                <SheetHeader>
                  <SheetTitle>Stock Adjustment</SheetTitle>
                </SheetHeader>
                <form
                  onSubmit={handleStockAdjustment}
                  className='space-y-4 mt-6'
                >
                  <div className='space-y-2'>
                    <Label htmlFor='newQuantity'>New Stock Quantity *</Label>
                    <Input
                      id='newQuantity'
                      type='number'
                      value={adjustmentForm.quantity}
                      onChange={e =>
                        setAdjustmentForm(prev => ({
                          ...prev,
                          quantity: parseFloat(e.target.value) || 0,
                        }))
                      }
                      min='0'
                      step='0.01'
                      required
                    />
                    <div className='text-xs text-gray-500'>
                      Current: {formatNumber(inventoryItem.quantity)}{' '}
                      {String(inventoryItem.measurementUnit) || 'units'}
                    </div>
                  </div>

                  <div className='grid grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label htmlFor='reason'>Adjustment Reason *</Label>
                      <Select
                        value={adjustmentForm.reason}
                        onValueChange={value =>
                          setAdjustmentForm(prev => ({
                            ...prev,
                            reason: value as any,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ADJUSTMENT_REASONS.map(reason => (
                            <SelectItem key={reason.value} value={reason.value}>
                              {reason.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className='space-y-2'>
                      <Label htmlFor='reference'>Reference Number *</Label>
                      <Input
                        id='reference'
                        value={adjustmentForm.reference}
                        onChange={e =>
                          setAdjustmentForm(prev => ({
                            ...prev,
                            reference: e.target.value,
                          }))
                        }
                        placeholder='ADJ-001'
                        required
                      />
                    </div>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='adjustmentNotes'>Notes *</Label>
                    <Textarea
                      id='adjustmentNotes'
                      value={adjustmentForm.notes}
                      onChange={e =>
                        setAdjustmentForm(prev => ({
                          ...prev,
                          notes: e.target.value,
                        }))
                      }
                      placeholder='Reason for adjustment...'
                      rows={3}
                      required
                    />
                  </div>

                  <Separator />

                  <div className='flex justify-end gap-2'>
                    <Button
                      type='button'
                      hierarchy='outlined'
                      onClick={() => setActiveSheet(null)}
                    >
                      Cancel
                    </Button>
                    <Button type='submit' loading={loading}>
                      Update Stock
                    </Button>
                  </div>
                </form>
              </SheetContent>
            </Sheet>
          </div>
        </CardContent>
      </Card>

      {/* Stock Alerts */}
      {(stockStats.availableStock <= inventoryItem.reorderLevel ||
        stockStats.expiredReservations > 0) && (
        <Alert>
          <WarningIcon className='h-4 w-4' />
          <AlertDescription>
            {stockStats.availableStock <= inventoryItem.reorderLevel && (
              <div>
                Low available stock: {formatNumber(stockStats.availableStock)}{' '}
                remaining.
              </div>
            )}
            {stockStats.expiredReservations > 0 && (
              <div>
                You have {stockStats.expiredReservations} expired reservations
                to review.
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Reservations Table */}
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <LockIcon className='h-5 w-5 text-blue-500' />
            Stock Reservations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            data={reservations}
            columns={reservationColumns}
            rowActions={reservationRowActions}
            emptyConfig={{
              title: 'No Stock Reservations',
              description: 'Reserve stock for orders to ensure availability.',
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
};
